import { themePurpleStardust, themeColorPresets } from "kz-ui-sdk/dist/themes/colors/presets";

/**
 * Build-time import map so Vite can split each theme into its own CSS chunk (no /sdk-themes URLs).
 *
 * We derive ids from the CSS filenames (e.g. `amber.css` -> `amber`) and validate
 * them against the SDK's `themeColorPresets` so we stay in sync automatically.
 */
const themeCssImports = import.meta.glob("/node_modules/kz-ui-sdk/dist/themes/*.css");

const themeCssById: Record<string, () => Promise<unknown>> = Object.fromEntries(
  Object.entries(themeCssImports).flatMap(([path, importer]) => {
    const fileName = path.split("/").pop();
    if (!fileName) return [];
    const themeId = fileName.replace(/\.css$/u, "");
    if (!themeColorPresets[themeId]) return [];
    return [[themeId, importer]];
  }),
);

const loadedThemeIds = new Set<string>();

function resolveThemeId(themeId: string): string {
  return themeCssById[themeId] ? themeId : themePurpleStardust.id;
}

/**
 * Loads the SDK theme stylesheet via the bundler. Safe to call multiple times per id (import is cached).
 */
export async function loadSdkThemeCss(themeId: string): Promise<void> {
  const resolved = resolveThemeId(themeId);
  if (loadedThemeIds.has(resolved)) return;
  loadedThemeIds.add(resolved);
  await themeCssById[resolved]();
}

function readThemeIdFromMemberSettings(): string | undefined {
  try {
    const raw = localStorage.getItem("member-settings");
    if (!raw) return undefined;
    const { themeId } = JSON.parse(raw) as { themeId?: string };
    return themeId || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Initial theme CSS before React mounts (matches index.html body-class / localStorage logic).
 */
export async function loadSdkThemeCssForBootstrap(): Promise<void> {
  const envDefault = import.meta.env.VITE_THEME_ID_DEFAULT as string;
  const themeId = readThemeIdFromMemberSettings() ?? envDefault ?? themePurpleStardust.id;
  await loadSdkThemeCss(themeId);
}
